import { docSlugFromPageId } from "@repo/shared";
import type {
  AiMessage,
  AiModelResponse,
  AiToolCallDetail,
  AiWidget,
  AvailableModelsResponse,
  ChatErrorClass,
  ClientFile,
  UnreadCountResponse,
} from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronUp,
  Copy,
  Loader2,
  Lock,
  MessageSquarePlus,
  Paperclip,
  Pencil,
  RefreshCw,
  Send,
  Square,
  Trash2,
  User,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router";
import { clientAccessDenialText } from "@/components/clients/client-access-denial-text";
import { SupportButton } from "@/components/support/support-button";
import { Button } from "@/components/ui/button";
import { ApiError, get, postFormData } from "@/lib/api/_client";
import { clientAccessDenialFromBody, clientAccessDenialOf } from "@/lib/api/client-access-denial";
import { useFlowInstanceMetadata } from "@/lib/hooks/use-flow-instances/use-flow-instances";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { ReasoningBlock } from "./ai/reasoning-block";
import { ToolCallsBlock } from "./ai/tool-calls-block";
import { ContextBar, UsageBar } from "./ai/usage-bar";
import { Widget } from "./ai/widgets/widget";

// Note: chat uses streaming SSE, so we can't use the _client helpers that parse JSON.
// We import API_BASE to construct the URL consistently.
const API_BASE = process.env.BUN_PUBLIC_API_URL;

import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  resolveTranscriptionLanguage,
  TranscriptionLanguageSelect,
} from "@/components/ui/transcription-language-select";
import {
  appLanguageToTranscriptionCode,
  VoiceInputButton,
  type VoiceInputState,
} from "@/components/ui/voice-input-button";
import {
  createThread,
  deleteThread,
  getThread,
  getUnreadCounts,
  markAllThreadsRead,
  markThreadRead,
} from "@/lib/api/ai-threads";
import { useSidebar } from "@/lib/context/sidebar-context";
import { useChatPrompts } from "@/lib/hooks/use-chat-prompts";
import {
  invalidateThreadsList,
  markAllThreadsReadInCache,
  markThreadReadInCache,
  removeThreadFromCache,
  useThreadsQuery,
} from "@/lib/hooks/use-threads-query";
import { useUserLanguage } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { dedupeWidgets, parseWidgets } from "@/lib/utils/parse-widgets";
import { T } from "./chat-panel.i18n";

/** Attached to a failed assistant turn — drives the error card + Retry. */
interface MessageErrorInfo {
  errorClass: ChatErrorClass;
  retryable: boolean;
  status?: number;
  provider?: string;
  model?: string;
  detail?: string;
  /** The user message to re-send when Retry is clicked. */
  retryMessage: string;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  widgets?: AiWidget[];
  toolDetails?: AiToolCallDetail[];
  reasoning?: string;
  errorInfo?: MessageErrorInfo;
  timestamp: number;
  usage?: {
    model: string;
    inputTokens: number;
    outputTokens: number;
    cost: number;
    durationMs: number;
    toolCalls: number;
  };
  context?: {
    systemPromptTokens: number;
    historyTokens: number;
    historyToolTokens?: number;
    historyMessages: number;
    toolDefsTokens: number;
    builtinTools: number;
    mcpTools: number;
    maxContextTokens: number;
  };
}

type ChatPanelProps = {
  chartColors?: string[] | null;
  /**
   * "global" = personal general threads,
   * "client" = shared client-scoped threads,
   * "system" = designer-only system-config threads (collections + products)
   */
  mode: "global" | "client" | "system";
  clientId?: string | null;
  clientName?: string | null;
  /** Transient flow instance context from URL (not stored on thread) */
  flowInstanceId?: string | null;
};

type ViewMode = "thread-list" | "chat";

export function ChatPanel({
  chartColors,
  mode,
  clientId,
  clientName,
  flowInstanceId,
}: ChatPanelProps) {
  const { openTab, openAiSettings } = useSidebar();
  const { formatDate } = useRegionalFormat();
  const { prompts: customPrompts } = useChatPrompts(mode);
  const queryClient = useQueryClient();
  const { metadata: flowMetadata } = useFlowInstanceMetadata(flowInstanceId ?? null);
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const navigate = useNavigate();

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>("thread-list");
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  // Model selection
  const [availableModels, setAvailableModels] = useState<
    Array<{
      id: string;
      name: string;
      providerName?: string;
      contextSize: number;
      inputPrice: string | null;
    }>
  >([]);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [modelLocked, setModelLocked] = useState(false); // locked after first message
  // The open thread's own model, from the server. It may not be in the picker
  // list (hidden behind an "(auto)" aggregate, disabled or removed) — the
  // picker still has to name it instead of saying "Select model".
  const [threadModel, setThreadModel] = useState<AiModelResponse | null>(null);
  const pickerModels =
    threadModel && !availableModels.some((m) => m.id === threadModel.id)
      ? [threadModel, ...availableModels]
      : availableModels;

  // Thread list — managed by React Query (enabled only while the list view is
  // open). Cache writes/invalidation go through the use-threads-query helpers.
  const {
    threads: threadList,
    isLoading: threadListLoading,
    isError: threadListError,
    isFetching: threadListFetching,
    refetch: refetchThreads,
  } = useThreadsQuery(mode, clientId, viewMode === "thread-list");

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  // Transcription language lives here so the picker can sit on the left of
  // the toolbar while the mic sits on the right.
  const [voiceLanguage, setVoiceLanguage] = useState(() =>
    resolveTranscriptionLanguage(appLanguageToTranscriptionCode(userLanguage)),
  );
  const [voiceState, setVoiceState] = useState<VoiceInputState>("idle");
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingReasoning, setStreamingReasoning] = useState("");
  const [streamingToolDetails, setStreamingToolDetails] = useState<AiToolCallDetail[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ─── File upload (client + system scopes) ─────────────────
  // Client mode → /internal/client-files/{clientId}; System mode → /internal/user-files
  // Both share the same chip + polling UX. The agent picks them up via
  // file_read (client) / user_file_read (system) using the id we attach to the
  // outgoing message.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [pendingUploads, setPendingUploads] = useState<
    Array<{ id: string; fileName: string; processingStatus: ClientFile["processingStatus"] }>
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const supportsFileUpload = mode === "system" || (mode === "client" && !!clientId);
  const uploadUrl =
    mode === "system"
      ? "/internal/user-files"
      : mode === "client" && clientId
        ? `/internal/client-files/${clientId}`
        : null;

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0 || !uploadUrl) return;
    setIsUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const created = await postFormData<ClientFile>(uploadUrl, formData);
        setPendingUploads((prev) => [
          ...prev,
          {
            id: created.id,
            fileName: created.fileName,
            processingStatus: created.processingStatus,
          },
        ]);
      }
      // Refresh files list (tab + agent tools share this query key)
      if (mode === "client" && clientId) {
        queryClient.invalidateQueries({ queryKey: ["clients", clientId, "files"] });
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.uploadFailed;
      setUploadError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const removePendingUpload = (id: string) => {
    setPendingUploads((prev) => prev.filter((u) => u.id !== id));
  };

  // Poll status of any pending uploads that aren't terminal yet, so the chip
  // Auto-resize the input textarea on every value change — covers both typing
  // and programmatic updates (e.g. clicking a suggested prompt).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [input]);

  // updates live and Send unlocks once transcription is done.
  const hasInFlightUpload = pendingUploads.some(
    (u) => u.processingStatus === "pending" || u.processingStatus === "processing",
  );
  useEffect(() => {
    if (!hasInFlightUpload) return;
    const id = setInterval(async () => {
      const inFlightIds = pendingUploads
        .filter((u) => u.processingStatus === "pending" || u.processingStatus === "processing")
        .map((u) => u.id);
      if (inFlightIds.length === 0) return;
      try {
        const updates = await Promise.all(
          inFlightIds.map((fid) =>
            get<ClientFile>(
              mode === "system"
                ? `/internal/user-files/${fid}`
                : `/internal/client-files/single/${fid}`,
            ),
          ),
        );
        setPendingUploads((prev) =>
          prev.map((u) => {
            const fresh = updates.find((f) => f.id === u.id);
            return fresh ? { ...u, processingStatus: fresh.processingStatus } : u;
          }),
        );
        // Refresh files tab too while we're polling (client scope only)
        if (mode === "client" && clientId) {
          queryClient.invalidateQueries({ queryKey: ["clients", clientId, "files"] });
        }
      } catch (err) {
        log.error({ err }, "Failed to poll pending upload status");
      }
    }, 5000);
    return () => clearInterval(id);
  }, [hasInFlightUpload, pendingUploads, clientId, mode, queryClient]);

  // Reset state when clientId changes (navigating between clients)
  useEffect(() => {
    setViewMode("thread-list");
    setActiveThreadId(null);
    setMessages([]);
    setThreadModel(null);
    setStreamingContent("");
  }, [clientId]);

  // ─── Thread list ────────────────────────────────────────────

  // When the badge count for our scope ticks up (background poll detects new
  // messages from a scheduled agent run, etc.), refresh the thread list and
  // the active thread so the panel doesn't show stale data on next open.
  // Shares the cache with Sidebar's poll. queryFn is required so that
  // invalidateQueries-triggered refetches on this observer have a function
  // to call — without it React Query logs "No queryFn was passed" on every
  // mark-read invalidation. QL-74 — shared client threads have no count, so
  // this refresh only fires for the personal scopes.
  const { data: unreadData } = useQuery<UnreadCountResponse>({
    queryKey: ["ai-threads", "unread-count"],
    queryFn: getUnreadCounts,
    enabled: false,
  });
  const scopeUnread =
    mode === "global"
      ? (unreadData?.global ?? 0)
      : mode === "system"
        ? (unreadData?.system ?? 0)
        : 0;
  const prevScopeUnreadRef = useRef(scopeUnread);
  useEffect(() => {
    const prev = prevScopeUnreadRef.current;
    prevScopeUnreadRef.current = scopeUnread;
    if (scopeUnread <= prev) return;
    invalidateThreadsList(queryClient, mode, clientId);
    if (activeThreadId) {
      getThread(activeThreadId)
        .then((thread) => setMessages(thread.messages.map(mapStoredMessage)))
        .catch((err) => log.warn({ err }, "Failed to refresh active thread on unread tick"));
    }
  }, [scopeUnread, activeThreadId, queryClient, mode, clientId]);

  // Load available models via React Query (auto-refetches on window focus)
  const { data: fetchedModels } = useQuery({
    queryKey: ["ai-models", "available"],
    queryFn: () => get<AvailableModelsResponse>("/internal/tenant/ai-models/models/available"),
    staleTime: 60 * 60 * 1000, // 60 min — invalidated by settings page on edit
  });

  useEffect(() => {
    if (fetchedModels && fetchedModels.models.length > 0) {
      setAvailableModels(fetchedModels.models);
      if (!selectedModelId) {
        const defaultId = fetchedModels.defaultChatModelId;
        const defaultIsAvailable =
          defaultId !== null && fetchedModels.models.some((m) => m.id === defaultId);
        setSelectedModelId(defaultIsAvailable ? defaultId : (fetchedModels.models[0]?.id ?? null));
      }
    }
  }, [fetchedModels]);

  useEffect(() => {
    if (mode !== "global") return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId: string }>).detail;
      if (detail?.threadId) handleOpenThread(detail.threadId);
    };
    window.addEventListener("open-ai-agent-thread", handler);
    return () => window.removeEventListener("open-ai-agent-thread", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const [newThreadPending, setNewThreadPending] = useState(false);

  /** QL-380 — the no-access text when a thread call was refused for a restricted client. */
  const accessDeniedText = (error: unknown): string | null => {
    const denial = clientAccessDenialOf(error);
    if (!denial) return null;
    const text = clientAccessDenialText(denial, userLanguage);
    return `${text.title}. ${text.message}`;
  };

  const handleNewThread = async () => {
    if (newThreadPending) return;
    setNewThreadPending(true);
    try {
      const { id } = await createThread({
        scope: mode,
        ...(mode === "client" && clientId ? { clientId } : {}),
      });
      setActiveThreadId(id);
      setMessages([]);
      setModelLocked(false);
      setThreadModel(null);
      // A model carried over from an opened thread may not be offered for new
      // chats (e.g. hidden behind an "(auto)" aggregate) — fall back to the default.
      if (!availableModels.some((m) => m.id === selectedModelId)) {
        const defaultId = fetchedModels?.defaultChatModelId;
        setSelectedModelId(
          defaultId && availableModels.some((m) => m.id === defaultId)
            ? defaultId
            : (availableModels[0]?.id ?? null),
        );
      }
      setViewMode("chat");
    } catch (error) {
      log.error({ err: error }, "Failed to create thread");
      // Surface the failure in the chat area instead of silently doing nothing.
      setMessages([
        {
          id: Date.now().toString(),
          role: "assistant",
          content: accessDeniedText(error) ?? t.errorMessage,
          timestamp: Date.now(),
        },
      ]);
    } finally {
      setNewThreadPending(false);
    }
  };

  // QL-74 — the server never flags a shared client thread unread, so this
  // (and the button it backs) only ever shows in the personal scopes.
  const hasUnread = threadList.some((t) => t.unread);

  const handleMarkAllRead = () => {
    markAllThreadsReadInCache(queryClient, mode, clientId);
    markAllThreadsRead(mode)
      .then(() => queryClient.invalidateQueries({ queryKey: ["ai-threads", "unread-count"] }))
      .catch((err) => log.warn({ err }, "markAllThreadsRead failed"));
  };

  const handleOpenThread = async (threadId: string) => {
    setActiveThreadId(threadId);
    setViewMode("chat");
    try {
      const thread = await getThread(threadId);
      setMessages(thread.messages.map(mapStoredMessage));
      // Lock model if thread has any messages (model was already chosen)
      setModelLocked(thread.messages.length > 0);
      // The thread's model, even when the picker list no longer offers it.
      setThreadModel(thread.modelInfo);
      if (thread.modelInfo) {
        setSelectedModelId(thread.modelInfo.id);
      } else if (thread.model && availableModels.some((m) => m.id === thread.model)) {
        setSelectedModelId(thread.model);
      }
      // Clear unread state. Optimistic local update so the dot disappears
      // immediately; server-side write is async and the badge query refetches.
      markThreadReadInCache(queryClient, mode, clientId, threadId);
      markThreadRead(threadId)
        .then(() => queryClient.invalidateQueries({ queryKey: ["ai-threads", "unread-count"] }))
        .catch((err) => log.warn({ err }, "markThreadRead failed"));
    } catch (error) {
      log.error({ err: error }, "Failed to load thread");
      const denied = accessDeniedText(error);
      setMessages(
        denied
          ? [
              {
                id: Date.now().toString(),
                role: "assistant",
                content: denied,
                timestamp: Date.now(),
              },
            ]
          : [],
      );
    }
  };

  const handleBackToList = () => {
    setViewMode("thread-list");
    setActiveThreadId(null);
    setMessages([]);
    setThreadModel(null);
    setStreamingContent("");
  };

  const handleDeleteThread = async (e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    try {
      await deleteThread(threadId);
      removeThreadFromCache(queryClient, mode, clientId, threadId);
      if (activeThreadId === threadId) {
        handleBackToList();
      }
    } catch (error) {
      log.error({ err: error }, "Failed to delete thread");
    }
  };

  // ─── Navigation handlers ──────────────────────────────────

  const handleSourceClick = (source: { pageId: string; title: string; slug: string }) => {
    openTab("docs");
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("navigate-to-doc", {
          detail: { slug: docSlugFromPageId(source.pageId), title: source.title },
        }),
      );
    }, 100);
  };

  const handleClientClick = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  const handleFlowInstanceClick = (url: string) => {
    navigate(url);
  };

  const handleActivityClick = (url: string) => {
    navigate(url);
  };

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  // ─── Send message ─────────────────────────────────────────

  const handleSend = async () => {
    // Allow sending if there's text OR pending uploads to announce
    if ((!input.trim() && pendingUploads.length === 0) || isLoading) return;

    // Prepend a compact one-liner per attached file. We block Send until
    // transcription is done, so by the time we get here, agent can call
    // file_read (mode get) directly without waiting.
    const uploadsBlock =
      pendingUploads.length > 0
        ? `${pendingUploads.map((u) => `📎 Attached: ${u.fileName} (file id: ${u.id})`).join("\n")}\n\n`
        : "";
    const messageText = `${uploadsBlock}${input.trim()}`.trim();
    // Clear pending uploads now — they're committed to this message
    if (pendingUploads.length > 0) setPendingUploads([]);

    setInput("");
    await sendMessage(messageText);
  };

  /**
   * Send a message on the active thread (creating one if needed) and stream
   * the reply. `retry` re-sends a previously failed message: the server skips
   * re-saving the user turn and the panel skips the duplicate user bubble.
   */
  const sendMessage = async (messageText: string, opts?: { retry?: boolean }) => {
    // If no active thread, create one first
    let threadId = activeThreadId;
    if (!threadId) {
      try {
        const { id } = await createThread({
          scope: mode,
          ...(mode === "client" && clientId ? { clientId } : {}),
        });
        threadId = id;
        setActiveThreadId(id);
        setViewMode("chat");
      } catch (error) {
        log.error({ err: error }, "Failed to create thread");
        const denied = accessDeniedText(error);
        if (denied) {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now().toString(),
              role: "assistant",
              content: denied,
              timestamp: Date.now(),
            },
          ]);
        }
        return;
      }
    }

    if (opts?.retry) {
      // Drop previous error cards — the retry replaces them.
      setMessages((prev) => prev.filter((m) => !m.errorInfo));
    } else {
      const userMessage: Message = {
        id: Date.now().toString(),
        role: "user",
        content: messageText,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMessage]);
    }
    setIsLoading(true);
    setModelLocked(true); // Lock model after first send
    setStreamingContent("");
    setStreamingReasoning("");
    setStreamingToolDetails([]);

    // Collected from the SSE error event (if any) so the catch below can show
    // a specific error card rather than the generic fallback.
    let streamErrorInfo: Omit<MessageErrorInfo, "retryMessage"> | null = null;

    try {
      const response = await fetch(`${API_BASE}/internal/ai/threads/${threadId}/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: messageText,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(flowInstanceId && { flowInstanceId }),
          ...(!modelLocked && selectedModelId && { modelId: selectedModelId }),
          ...(mode === "system" && { scope: "system" as const }),
          ...(opts?.retry && { retry: true }),
        }),
      });

      if (!response.ok) {
        streamErrorInfo = await classifyHttpFailure(response);
        throw new Error(streamErrorInfo.detail || t.errorMessage);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error(t.errorMessage);
      }

      let fullContent = "";
      const widgetList: AiWidget[] = [];
      const toolDetails: AiToolCallDetail[] = [];
      let usage: Message["usage"] | undefined;
      let streamError: string | null = null;
      let contextInfo: Message["context"] | undefined;
      let wasCancelled = false;

      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        // Keep the trailing fragment (no terminating newline) for the next read
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === "reasoning") {
                setStreamingReasoning((prev) => prev + data.content);
              } else if (data.type === "content") {
                fullContent += data.content;
                setStreamingContent(fullContent);
              } else if (data.type === "discard_content") {
                fullContent = "";
                setStreamingContent("");
              } else if (data.type === "tool_start") {
                toolDetails.push({
                  toolCallId: data.toolCallId,
                  name: data.name,
                  args: data.args,
                  status: "running",
                });
                setStreamingToolDetails([...toolDetails]);
              } else if (data.type === "tool_result") {
                const td = toolDetails.find((t) => t.toolCallId === data.toolCallId);
                if (td) {
                  td.status = data.success ? "completed" : "error";
                  td.result = data.result;
                  td.success = data.success;
                  td.durationMs = data.durationMs;
                }
                setStreamingToolDetails([...toolDetails]);
                // Invalidate React Query caches when a write tool modifies data
                if (data.invalidateKeys?.length) {
                  log.info({ keys: data.invalidateKeys }, "Invalidating keys");
                  for (const key of data.invalidateKeys) {
                    queryClient.invalidateQueries({ queryKey: key });
                  }
                }
                // If the tool deleted the flow instance the user is currently viewing,
                // navigate back to the client's flow list — the open page no longer exists.
                if (
                  data.deletedFlowInstanceId &&
                  data.deletedFlowInstanceId === flowInstanceId &&
                  clientId
                ) {
                  navigate(`/clients/${clientId}?tab=flows`);
                }
              } else if (data.type === "widget") {
                widgetList.push(data.widget as AiWidget);
              } else if (data.type === "error") {
                streamError = data.detail || data.error || "Chat error";
                streamErrorInfo = {
                  errorClass: (data.errorClass as ChatErrorClass) || "unknown",
                  retryable: data.retryable ?? true,
                  status: data.status,
                  provider: data.provider,
                  model: data.model,
                  detail: data.detail || data.error,
                };
              } else if (data.type === "cancelled") {
                wasCancelled = true;
              } else if (data.type === "done") {
                usage = {
                  model: data.provider,
                  inputTokens: data.tokens.input,
                  outputTokens: data.tokens.output,
                  cost: data.cost.total,
                  durationMs: data.timing.total,
                  toolCalls: data.toolCalls,
                };
                if (data.context) {
                  contextInfo = data.context;
                }
              }
            } catch (e) {
              log.error({ err: e }, "Error parsing SSE data");
            }
          }
        }
      }

      if (streamError) {
        throw new Error(streamError);
      }

      // Strip <think> tags from content and extract inline reasoning
      let cleanContent = fullContent;
      let inlineReasoning = "";
      const thinkMatch = fullContent.match(/<think>([\s\S]*?)<\/think>/g);
      if (thinkMatch) {
        for (const m of thinkMatch) {
          inlineReasoning += `${m.replace(/<\/?think>/g, "").trim()}\n`;
          cleanContent = cleanContent.replace(m, "");
        }
      }
      // Also strip unclosed <think> tags (model still thinking)
      cleanContent = cleanContent.replace(/<think>[\s\S]*$/, "").trim();

      // Combine streaming reasoning + inline reasoning
      const fullReasoning = (streamingReasoning + inlineReasoning).trim();

      // Parse widget tags from AI response text
      const parsed = parseWidgets(cleanContent);
      const allWidgets = dedupeWidgets([...widgetList, ...parsed.widgets]);

      const finalText = wasCancelled
        ? `${parsed.cleanText}${parsed.cleanText ? "\n\n" : ""}_Stopped by user._`
        : parsed.cleanText;

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: finalText,
        widgets: allWidgets.length > 0 ? allWidgets : undefined,
        toolDetails: toolDetails.length > 0 ? toolDetails : undefined,
        reasoning: fullReasoning || undefined,
        usage,
        context: contextInfo,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setStreamingContent("");
      setStreamingReasoning("");

      // Sending a message bumps the thread's lastMessageAt, which would re-mark
      // the thread "unread" for the very user who just chatted in it. Clear
      // that immediately after the assistant reply lands.
      if (activeThreadId) {
        markThreadRead(activeThreadId)
          .then(() => queryClient.invalidateQueries({ queryKey: ["ai-threads", "unread-count"] }))
          .catch((err) => log.warn({ err }, "post-send markThreadRead failed"));
      }
    } catch (error) {
      log.error({ err: error, errorInfo: streamErrorInfo }, "Chat error");
      const info: MessageErrorInfo = {
        // No structured info means the connection itself broke (network drop,
        // server crash) — worth retrying.
        errorClass: streamErrorInfo?.errorClass ?? "unknown",
        retryable: streamErrorInfo?.retryable ?? true,
        status: streamErrorInfo?.status,
        provider: streamErrorInfo?.provider,
        model: streamErrorInfo?.model,
        detail: streamErrorInfo?.detail,
        retryMessage: messageText,
      };
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: t.errorHeadline[info.errorClass] ?? t.errorMessage,
        errorInfo: info,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
      setIsStopping(false);
    }
  };

  /** Map a non-OK HTTP response (pre-stream) to structured error info. */
  const classifyHttpFailure = async (
    response: Response,
  ): Promise<Omit<MessageErrorInfo, "retryMessage">> => {
    let detail: string | undefined;
    try {
      const body = await response.json();
      detail = typeof body?.error === "string" ? body.error : undefined;
      const denial = response.status === 403 ? clientAccessDenialFromBody(body) : null;
      if (denial) {
        const text = clientAccessDenialText(denial, userLanguage);
        detail = `${text.title}. ${text.message}`;
      }
    } catch {
      // no JSON body
    }
    if (response.status === 429) {
      const retryAfter = response.headers.get("Retry-After");
      return {
        errorClass: "rate_limited",
        retryable: true,
        status: 429,
        detail: retryAfter ? `${detail ?? ""} (retry in ~${retryAfter}s)`.trim() : detail,
      };
    }
    if (response.status >= 500) {
      return { errorClass: "provider_down", retryable: true, status: response.status, detail };
    }
    // 400/403/404/410 — request-level rejections (permissions, legacy model, …)
    return { errorClass: "unknown", retryable: false, status: response.status, detail };
  };

  const handleRetry = async (msg: Message) => {
    if (isLoading || !msg.errorInfo) return;
    await sendMessage(msg.errorInfo.retryMessage, { retry: true });
  };

  // Send the cancel request. The streamChat loop polls Redis at safe
  // boundaries; whatever has streamed so far is persisted with "_Stopped by
  // user._" appended and metadata.cancelled = true. We don't abort the SSE
  // reader here — we want the cancelled event + final state from the server.
  const handleStop = async () => {
    if (!activeThreadId || !isLoading || isStopping) return;
    setIsStopping(true);
    try {
      const response = await fetch(`${API_BASE}/internal/ai/threads/${activeThreadId}/cancel`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        log.error({ status: response.status }, "Cancel request failed");
        setIsStopping(false);
      }
    } catch (error) {
      log.error({ err: error }, "Cancel request errored");
      setIsStopping(false);
    }
  };

  // Check if context is near limit (80%)
  const lastMsgWithContext = [...messages].reverse().find((m) => m.context);
  const ctx = lastMsgWithContext?.context;
  const estimatedNextContext = ctx
    ? ctx.systemPromptTokens + ctx.historyTokens + ctx.toolDefsTokens
    : 0;
  const maxContext = ctx?.maxContextTokens ?? 256000;
  const contextFull = estimatedNextContext > maxContext * 0.8;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Thread list view ─────────────────────────────────────

  if (viewMode === "thread-list") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-6 h-16">
          <h2 className="text-xl font-semibold truncate">
            {mode === "system"
              ? t.systemAi
              : mode === "client"
                ? clientName || t.aiAgent
                : t.aiAgent}
          </h2>
          <div className="flex items-center gap-1.5">
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                className="text-xs gap-1.5"
              >
                {t.markAllRead}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleNewThread}
              disabled={newThreadPending}
              className="text-xs gap-1.5"
            >
              {t.newThread}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-1.5 px-6 pb-2 text-[11px] text-muted-foreground">
          {mode === "client" ? (
            <>
              <Users className="h-3 w-3" />
              <span>{t.visibilityShared}</span>
            </>
          ) : (
            <>
              <Lock className="h-3 w-3" />
              <span>{t.visibilityPrivate}</span>
            </>
          )}
        </div>

        <Separator />

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {threadListLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : threadListError && threadList.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">{t.loadThreadsFailed}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchThreads()}
                className="gap-1.5"
                loading={threadListFetching}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {t.retry}
              </Button>
            </div>
          ) : threadList.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <p className="text-sm text-muted-foreground mb-2">
                {mode === "system"
                  ? t.noThreadsSystem
                  : mode === "client"
                    ? t.noThreadsClient
                    : t.noThreadsGlobal}
              </p>
              <p className="text-xs text-muted-foreground">
                {mode === "system"
                  ? t.greetingSystem
                  : mode === "client"
                    ? t.greetingClient
                    : t.greetingGlobal}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {threadList.map((thread) => (
                <button
                  type="button"
                  key={thread.id}
                  className="w-full text-left p-3 rounded-md hover:bg-muted/50 transition-colors cursor-pointer group"
                  onClick={() => handleOpenThread(thread.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium truncate flex-1 min-w-0 flex items-center gap-1.5">
                      {thread.unread && (
                        <span
                          role="status"
                          aria-label={t.unread}
                          className="inline-block w-2 h-2 rounded-full bg-[hsl(var(--brand))] flex-shrink-0"
                        />
                      )}
                      <span className="truncate">{thread.title || t.untitled}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-[10px] text-muted-foreground/60 whitespace-nowrap">
                        {formatRelativeTime(thread.lastMessageAt, formatDate)}
                      </span>
                      {/* biome-ignore lint/a11y/useKeyWithClickEvents: nested inside parent button */}
                      {/* biome-ignore lint/a11y/noStaticElementInteractions: can't nest <button> in <button> */}
                      <div
                        className="inline-flex items-center justify-center h-6 w-6 rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent"
                        onClick={(e) => handleDeleteThread(e, thread.id)}
                      >
                        <Trash2 className="h-3 w-3 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                  {(thread.lastMessagePreview || thread.createdByName) && (
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {mode === "client" && thread.createdByName && (
                        <span className="mr-1.5">{thread.createdByName}:</span>
                      )}
                      {thread.lastMessagePreview
                        ?.replace(/<think>[\s\S]*?<\/think>/g, "")
                        .replace(/<[^>]+>[^<]*<\/[^>]+>/g, "")
                        .replace(/<[^>]+>/g, "")
                        .replace(
                          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
                          "",
                        )
                        .replace(/[,\s]{2,}/g, " ")
                        .trim() || "..."}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Chat view ────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Panel Header */}
      <div className="flex items-center justify-between px-4 h-16 gap-2">
        <Button variant="ghost" size="icon" tooltip={t.back} onClick={handleBackToList}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-sm font-medium truncate flex-1">
          {mode === "system" ? t.systemAi : mode === "client" ? clientName || t.aiAgent : t.aiAgent}
        </h2>
        {activeThreadId && (
          <SupportButton
            entityType="chat_thread"
            entityId={activeThreadId}
            displayName={threadList.find((x) => x.id === activeThreadId)?.title || t.aiAgent}
            size="icon"
          />
        )}
        <Button
          variant="ghost"
          size="icon"
          tooltip={t.newThread}
          onClick={handleNewThread}
          disabled={newThreadPending}
          className="text-xs gap-1.5 h-8"
        >
          <MessageSquarePlus className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex items-center gap-1.5 px-4 pb-2 text-[11px] text-muted-foreground">
        {mode === "client" ? (
          <>
            <Users className="h-3 w-3" />
            <span>{t.visibilityShared}</span>
          </>
        ) : (
          <>
            <Lock className="h-3 w-3" />
            <span>{t.visibilityPrivate}</span>
          </>
        )}
      </div>

      <Separator />

      {/* Messages Area */}
      <ScrollArea className="flex-1">
        <div className="w-full px-6 py-4 space-y-4">
          {messages.length === 0 && !streamingContent && (
            <div className="flex flex-col py-8">
              <p className="text-sm text-muted-foreground mb-4">
                {mode === "system"
                  ? t.greetingSystem
                  : mode === "client"
                    ? t.greetingClient
                    : t.greetingGlobal}
              </p>
              {customPrompts.length === 0 && (
                <p className="text-xs text-muted-foreground mb-3">{t.tryAsking}</p>
              )}
              <div className="space-y-2">
                {(() => {
                  const defaults =
                    mode === "system"
                      ? [
                          t.exampleSystemCollections,
                          t.exampleSystemProductDetail,
                          t.exampleSystemAllocationAudit,
                          t.exampleSystemCreateProduct,
                        ]
                      : mode === "client"
                        ? [
                            t.exampleClient360,
                            t.exampleClientWealth,
                            t.exampleClientFlowStatus,
                            t.exampleClientMeetingPrep,
                            t.exampleClientEnin,
                            t.exampleClientMail,
                          ]
                        : [
                            t.exampleGlobalBreakdown,
                            t.exampleGlobalRecentActivity,
                            t.exampleGlobalNewsRadar,
                            t.exampleGlobalDocsSearch,
                            t.exampleGlobalEninLookup,
                            t.exampleGlobalDormantClients,
                          ];
                  const items =
                    customPrompts.length > 0
                      ? customPrompts.map((p) => ({ key: p.id, label: p.title, send: p.text }))
                      : defaults.map((d) => ({ key: d.label, label: d.label, send: d.send }));
                  return items.map((item) => (
                    <div key={item.key} className="group flex items-stretch gap-1">
                      <button
                        type="button"
                        className="flex-1 text-left text-xs p-3 rounded-md bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                        onClick={() => setInput(item.send)}
                      >
                        &quot;{item.label}&quot;
                      </button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="px-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => openAiSettings("prompts")}
                            aria-label={t.editPrompts}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{t.editPrompts}</TooltipContent>
                      </Tooltip>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div key={message.id}>
              {message.errorInfo ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm font-medium">
                        {t.errorHeadline[message.errorInfo.errorClass] ?? t.errorMessage}
                      </p>
                      {message.errorInfo.detail && (
                        <p className="text-xs text-muted-foreground break-words">
                          {message.errorInfo.detail}
                        </p>
                      )}
                      {(message.errorInfo.provider || message.errorInfo.status) && (
                        <p className="text-[10px] text-muted-foreground/70">
                          {[
                            message.errorInfo.provider,
                            message.errorInfo.model,
                            message.errorInfo.status ? `HTTP ${message.errorInfo.status}` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {t.errorHint[message.errorInfo.errorClass]}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5"
                    disabled={isLoading}
                    onClick={() => handleRetry(message)}
                  >
                    <RefreshCw className="h-3 w-3" />
                    {t.retryAction}
                  </Button>
                </div>
              ) : (
                <MessageBubble
                  message={message}
                  onSourceClick={handleSourceClick}
                  onClientClick={handleClientClick}
                  onFlowInstanceClick={handleFlowInstanceClick}
                  onActivityClick={handleActivityClick}
                  chartColors={chartColors}
                />
              )}
            </div>
          ))}

          {/* Streaming state — tool details, reasoning, content, widget placeholders, spinner */}
          {isLoading &&
            (() => {
              const displayContent = streamingContent
                ? streamingContent
                    .replace(/<think>[\s\S]*?<\/think>/g, "")
                    .replace(/<think>[\s\S]*$/, "")
                    .replace(
                      /<(client-widget|flow-widget|note-widget|call-widget|meeting-widget|piechart-widget|doc-widget)[^>]*>[\s\S]*?<\/\1>/g,
                      "",
                    )
                    .replace(
                      /<(client-widget|flow-widget|note-widget|call-widget|meeting-widget|piechart-widget|doc-widget)[^>]*>[\s\S]*$/g,
                      "",
                    )
                    .trim()
                : "";
              const hasWidgetInProgress = streamingContent
                ? /<(client-widget|flow-widget|note-widget|call-widget|meeting-widget|piechart-widget|doc-widget)/.test(
                    streamingContent,
                  )
                : false;
              const hasAnything =
                streamingToolDetails.length > 0 ||
                streamingReasoning ||
                displayContent ||
                hasWidgetInProgress;

              return (
                <div className="space-y-2">
                  {/* Tool calls — always visible during streaming */}
                  {streamingToolDetails.length > 0 && (
                    <ToolCallsBlock tools={streamingToolDetails} defaultOpen={true} />
                  )}

                  {/* Reasoning */}
                  {streamingReasoning && (
                    <ReasoningBlock text={streamingReasoning} defaultOpen={true} streaming={true} />
                  )}

                  {/* Text content (stripped of widgets/think tags) */}
                  {displayContent && (
                    <MessageBubble
                      message={{
                        id: "streaming",
                        role: "assistant",
                        content: displayContent,
                        timestamp: Date.now(),
                      }}
                      isStreaming={true}
                      onClientClick={handleClientClick}
                      onFlowInstanceClick={handleFlowInstanceClick}
                      onActivityClick={handleActivityClick}
                      chartColors={chartColors}
                    />
                  )}

                  {/* Widget loading placeholder */}
                  {hasWidgetInProgress && (
                    <div className="rounded-lg bg-muted animate-pulse h-20 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {/* Spinner when nothing else visible yet */}
                  {!hasAnything && (
                    <div className="flex items-start gap-3">
                      <div className="rounded-lg bg-muted px-4 py-3">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          <div ref={messagesEndRef} />
        </div>
        <ScrollBar orientation="vertical" />
      </ScrollArea>

      {/* Context bar + limit check */}
      {messages.length > 0 &&
        (() => {
          const lastMsg = [...messages].reverse().find((m) => m.context);
          // Even without context info, show totals if we have usage data
          const hasUsage = messages.some((m) => m.usage);
          if (!lastMsg?.context && !hasUsage) return null;
          const chatTotals = {
            totalInput: messages.reduce((s, m) => s + (m.usage?.inputTokens ?? 0), 0),
            totalOutput: messages.reduce((s, m) => s + (m.usage?.outputTokens ?? 0), 0),
            totalCost: messages.reduce((s, m) => s + (m.usage?.cost ?? 0), 0),
            peakContext: Math.max(...messages.map((m) => m.usage?.inputTokens ?? 0)),
          };
          return <ContextBar context={lastMsg?.context} chatTotals={chatTotals} />;
        })()}
      {flowInstanceId && (
        <div className="px-4 py-1.5 flex items-center gap-1.5 text-[10px] text-muted-foreground/70 border-t border-muted/30">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
          <span className="truncate">
            {flowMetadata?.name || "Active flow"}
            {flowMetadata?.progressPercent != null && ` · ${flowMetadata.progressPercent}%`}
          </span>
        </div>
      )}
      {/* Model picker — popover opening upward */}
      {pickerModels.length > 1 && (
        <ModelPicker
          models={pickerModels}
          selectedId={selectedModelId}
          onSelect={setSelectedModelId}
          locked={modelLocked || messages.length > 0 || isLoading}
        />
      )}
      <div className="border-t p-4 space-y-2">
        {supportsFileUpload && (pendingUploads.length > 0 || uploadError) && (
          <div className="space-y-1">
            {pendingUploads.map((u) => {
              const isInFlight =
                u.processingStatus === "pending" || u.processingStatus === "processing";
              const statusLabel =
                u.processingStatus === "pending"
                  ? t.statusQueued
                  : u.processingStatus === "processing"
                    ? t.statusTranscribing
                    : u.processingStatus === "failed"
                      ? t.statusFailed
                      : t.statusReady;
              return (
                <div
                  key={u.id}
                  className="flex items-center gap-2 text-xs bg-muted/60 border rounded-md px-2 py-1"
                >
                  <Paperclip className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{u.fileName}</span>
                  <span
                    className={`flex items-center gap-1 flex-shrink-0 ${
                      u.processingStatus === "failed"
                        ? "text-destructive"
                        : u.processingStatus === "done" || u.processingStatus === "skipped"
                          ? "text-success"
                          : "text-muted-foreground"
                    }`}
                  >
                    {isInFlight ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : u.processingStatus === "failed" ? (
                      <AlertCircle className="h-3 w-3" />
                    ) : (
                      <CheckCircle2 className="h-3 w-3" />
                    )}
                    {statusLabel}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => removePendingUpload(u.id)}
                        className="p-0.5 rounded hover:bg-accent"
                        aria-label={t.clear}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{t.clear}</TooltipContent>
                  </Tooltip>
                </div>
              );
            })}
            {hasInFlightUpload && (
              <div className="text-xs text-muted-foreground italic px-1">{t.waitingForFiles}</div>
            )}
            {uploadError && (
              <div className="text-xs text-destructive bg-destructive/10 px-2 py-1 rounded-md">
                {uploadError}
              </div>
            )}
          </div>
        )}
        {/* One field: the textarea on top, a single 32px toolbar underneath. */}
        <div
          className={`rounded-lg border bg-background transition-colors focus-within:border-muted-foreground/60 ${contextFull ? "opacity-50" : ""}`}
        >
          <textarea
            ref={inputRef}
            placeholder={t.askQuestion}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading || contextFull}
            rows={1}
            className="block min-h-[40px] w-full resize-none bg-transparent px-3.5 pt-3 pb-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none"
          />
          <div className="flex items-center justify-between p-1.5">
            <div className="flex items-center gap-0.5">
              {supportsFileUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <Button
                    onClick={handlePickFile}
                    disabled={isLoading || contextFull}
                    size="icon"
                    variant="ghost"
                    className="text-muted-foreground hover:text-foreground"
                    tooltip={t.attachFile}
                    loading={isUploading}
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                </>
              )}
              <TranscriptionLanguageSelect
                value={voiceLanguage}
                onChange={setVoiceLanguage}
                disabled={voiceState !== "idle"}
                label={t.voiceLanguage}
                size="md"
              />
            </div>
            <div className="flex items-center gap-1">
              {/* Voice-to-text: record → Mistral Voxtral. */}
              <VoiceInputButton
                onTranscript={(text) => setInput((prev) => (prev ? `${prev} ${text}` : text))}
                language={voiceLanguage}
                onLanguageChange={setVoiceLanguage}
                hideLanguagePicker
                onStateChange={setVoiceState}
                size="md"
                labels={{
                  start: t.voiceStart,
                  stop: t.voiceStop,
                  transcribing: t.statusTranscribing,
                  error: t.voiceError,
                  micDenied: t.voiceMicDenied,
                  language: t.voiceLanguage,
                }}
              />
              {isLoading ? (
                <Button
                  onClick={handleStop}
                  size="icon"
                  variant="destructive"
                  tooltip={t.stopGenerating}
                  loading={isStopping}
                >
                  <Square className="h-4 w-4" fill="currentColor" />
                </Button>
              ) : (
                <Button
                  onClick={handleSend}
                  disabled={
                    (!input.trim() && pendingUploads.length === 0) ||
                    contextFull ||
                    hasInFlightUpload
                  }
                  size="icon"
                  className="disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:shadow-none"
                  // The hint only applies while an upload is in flight; the rest of
                  // the time the button still has to say what it is.
                  tooltip={hasInFlightUpload ? t.waitingForFiles : t.sendMessage}
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Model Picker ──────────────────────────────────────────

function ModelPicker({
  models,
  selectedId,
  onSelect,
  locked,
}: {
  models: Array<{
    id: string;
    name: string;
    providerName?: string;
    contextSize: number;
    inputPrice: string | null;
    outputPrice?: string | null;
  }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  locked: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = models.find((m) => m.id === selectedId);

  return (
    <div className="px-4 py-1 border-t border-muted/30 relative">
      <button
        type="button"
        disabled={locked}
        className={`flex items-center gap-1.5 text-[11px] text-muted-foreground transition-colors ${locked ? "cursor-default" : "hover:text-foreground cursor-pointer"}`}
        onClick={() => !locked && setOpen((v) => !v)}
      >
        <span>
          {selected
            ? `${selected.providerName ? `${selected.providerName} / ` : ""}${selected.name}`
            : "Select model"}
        </span>
        {selected && (
          <span className="text-[10px] text-muted-foreground">
            {(selected.contextSize / 1000).toFixed(0)}K
            {selected.inputPrice ? ` · €${selected.inputPrice}/€${selected.outputPrice}` : ""}
          </span>
        )}
        {!locked && (
          <ChevronUp className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: dismiss overlay */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: dismiss overlay */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Menu opening upward */}
          <div className="absolute bottom-full left-4 right-4 mb-1 z-50 bg-popover border rounded-md shadow-lg max-h-[50vh] overflow-y-auto">
            {models.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`w-full text-left px-3 py-2 text-xs hover:bg-muted transition-colors flex items-center justify-between ${
                  m.id === selectedId ? "bg-muted" : ""
                }`}
                onClick={() => {
                  onSelect(m.id);
                  setOpen(false);
                }}
              >
                <span>
                  <span className="text-muted-foreground">
                    {m.providerName ? `${m.providerName} / ` : ""}
                  </span>
                  <span className="font-medium">{m.name}</span>
                </span>
                <span className="text-[10px] text-muted-foreground ml-2 whitespace-nowrap">
                  {(m.contextSize / 1000).toFixed(0)}K
                  {m.inputPrice ? ` · €${m.inputPrice}/€${m.outputPrice}` : ""}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────

function mapStoredMessage(msg: AiMessage): Message {
  const meta = msg.metadata;
  // Strip <think> tags and parse widget tags from stored content
  const rawContent = (msg.content || "").replace(/<think>[\s\S]*?<\/think>/g, "").trim();
  const parsed = parseWidgets(rawContent);
  const allWidgets = dedupeWidgets([...(meta?.widgets ?? []), ...parsed.widgets]);
  return {
    id: msg.id,
    role: msg.role as "user" | "assistant",
    content: parsed.cleanText,
    timestamp: new Date(msg.createdAt).getTime(),
    widgets: allWidgets.length > 0 ? allWidgets : undefined,
    toolDetails: meta?.toolCalls?.map((tc) => ({
      toolCallId: tc.name,
      name: tc.name,
      args: tc.args ?? {},
      result: tc.result,
      success: tc.success,
      durationMs: tc.durationMs,
      status: (tc.success != null ? (tc.success ? "completed" : "error") : "completed") as
        | "completed"
        | "error"
        | "running",
    })),
    reasoning: meta?.reasoning,
    usage: meta?.usage,
    context: meta?.context,
  };
}

function formatRelativeTime(dateStr: string, formatAbsoluteDate: (d: Date) => string): string {
  const now = Date.now();
  // DB timestamps are UTC but may lack Z suffix — ensure correct parsing
  const normalized = dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : `${dateStr}Z`;
  const date = new Date(normalized).getTime();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  // Older than a week: absolute date in the tenant's regional format (QL-108).
  return formatAbsoluteDate(new Date(normalized));
}

// ─── Message bubble ─────────────────────────────────────────

interface MessageBubbleProps {
  message: Message;
  isStreaming?: boolean;
  onSourceClick?: (source: { pageId: string; title: string; slug: string }) => void;
  onClientClick?: (clientId: string) => void;
  onFlowInstanceClick?: (url: string) => void;
  onActivityClick?: (url: string) => void;
  chartColors?: string[] | null;
}

function CopyButton({ text, isUser }: { text: string; isUser: boolean }) {
  const t = useT(T);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`absolute top-2 right-2 opacity-0 group-hover/msg:opacity-100 transition-all p-1 rounded ${
            isUser
              ? "bg-primary text-primary-foreground hover:bg-primary/80"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
          onClick={handleCopy}
          aria-label={copied ? t.copied : t.copy}
        >
          {copied ? (
            <span className="h-3 w-3 flex items-center justify-center text-[10px]">{"\u2713"}</span>
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
      </TooltipTrigger>
      {/* Follows the glyph: the button turns into a tick for 1.5s after a copy. */}
      <TooltipContent>{copied ? t.copied : t.copy}</TooltipContent>
    </Tooltip>
  );
}

function MessageBubble({
  message,
  isStreaming,
  onSourceClick,
  onClientClick,
  onFlowInstanceClick,
  onActivityClick,
  chartColors,
}: MessageBubbleProps) {
  const isUser = message.role === "user";

  return (
    <div className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%] min-w-0 space-y-2">
        {/* Tool calls - shown above message content */}
        {!isUser && message.toolDetails && message.toolDetails.length > 0 && (
          <ToolCallsBlock tools={message.toolDetails} />
        )}

        {/* Reasoning / thinking - shown after tools, before content */}
        {!isUser && message.reasoning && <ReasoningBlock text={message.reasoning} />}

        {/* Message bubble — hide if content is empty (widgets-only response) */}
        {message.content.trim() ? (
          <div
            className={`rounded-lg px-4 py-3 relative group/msg ${
              isUser ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
            }`}
          >
            <CopyButton text={message.content} isUser={isUser} />
            {isUser ? (
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            ) : (
              <div className="text-xs max-w-none leading-5 break-words overflow-hidden">
                <ReactMarkdown
                  components={{
                    p({ children }) {
                      return <div className="mb-2 last:mb-0">{children}</div>;
                    },
                    h1({ children }) {
                      return <div className="font-semibold mb-1 mt-2">{children}</div>;
                    },
                    h2({ children }) {
                      return <div className="font-semibold mb-1 mt-2">{children}</div>;
                    },
                    h3({ children }) {
                      return <div className="font-semibold mb-1 mt-1.5">{children}</div>;
                    },
                    ul({ children }) {
                      return <ul className="my-1 ml-4 list-disc space-y-0.5">{children}</ul>;
                    },
                    ol({ children }) {
                      return <ol className="my-1 ml-4 list-decimal space-y-0.5">{children}</ol>;
                    },
                    li({ children }) {
                      return <li className="leading-5">{children}</li>;
                    },
                    strong({ children }) {
                      return <strong className="font-semibold">{children}</strong>;
                    },
                    a({ href, children }: any) {
                      // Handle client links
                      if (href?.startsWith("client:")) {
                        const clientId = href.replace("client:", "");
                        return (
                          <button
                            type="button"
                            onClick={() => onClientClick?.(clientId)}
                            className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                          >
                            <User className="h-3 w-3" />
                            {children}
                          </button>
                        );
                      }
                      const clientMatch = href?.match(/clients\/([a-zA-Z0-9]+)/);
                      if (clientMatch) {
                        const clientId = clientMatch[1];
                        return (
                          <button
                            type="button"
                            onClick={() => onClientClick?.(clientId)}
                            className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                          >
                            <User className="h-3 w-3" />
                            {children}
                          </button>
                        );
                      }
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {children}
                        </a>
                      );
                    },
                    code({ node, inline, className, children, ...props }: any) {
                      return !inline ? (
                        <pre className="my-1.5 overflow-x-auto rounded bg-muted/60 px-3 py-2 text-[11px] font-mono leading-5">
                          <code>{String(children).replace(/\n$/, "")}</code>
                        </pre>
                      ) : (
                        <code
                          className="bg-muted/50 px-1.5 py-0.5 rounded text-xs font-mono"
                          {...props}
                        >
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            )}
            {isStreaming && (
              <span className="inline-block w-2 h-4 ml-1 bg-foreground/50 animate-pulse" />
            )}
          </div>
        ) : null}

        {/* Widgets */}
        {message.widgets && message.widgets.length > 0 && (
          <div className="space-y-2">
            {message.widgets.map((widget) => (
              <Widget
                key={`${widget.type}-${JSON.stringify(widget).slice(0, 50)}`}
                widget={widget}
                chartColors={chartColors}
                onClientClick={onClientClick}
                onSourceClick={onSourceClick}
                onFlowInstanceClick={onFlowInstanceClick}
                onActivityClick={onActivityClick}
              />
            ))}
          </div>
        )}

        {/* Usage stats */}
        {message.usage && (
          <UsageBar
            model={message.usage.model}
            inputTokens={message.usage.inputTokens}
            outputTokens={message.usage.outputTokens}
            cost={message.usage.cost}
            durationMs={message.usage.durationMs}
            toolCalls={message.usage.toolCalls}
          />
        )}
      </div>
    </div>
  );
}
